import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ShieldCheck, MapPin, Phone, Mail, Clock,
  Receipt, SearchCheck, BookOpenCheck, Building2, Check, Sparkles,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { MaskText } from "@/components/MaskText";
import { Marquee } from "@/components/Marquee";
import { ContactForm } from "@/components/ContactForm";
import { BRAND, HERO, STATS, APPROACH, TAX_UPDATES, IMAGES } from "@/data/content";
import { SERVICE_GROUPS, ALL_SERVICE_COUNT, PACKAGES, FEATURES, PRICE_NOTE } from "@/data/services";

const GROUP_ICONS = { receipt: Receipt, search: SearchCheck, book: BookOpenCheck, building: Building2 };

export default function Home() {
  const { t, isAr } = useLang();
  const Arrow = ({ className = "" }) => <ArrowRight className={`${className} ${isAr ? "rotate-180" : ""}`} />;

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", "-55%"]);

  const marqueeItems = [
    t({ en: "VAT", ar: "القيمة المضافة" }),
    t({ en: "Corporate Tax", ar: "ضريبة الشركات" }),
    t({ en: "Excise", ar: "الانتقائية" }),
    t({ en: "Audit", ar: "التدقيق" }),
    t({ en: "Bookkeeping", ar: "مسك الدفاتر" }),
    t({ en: "Company Setup", ar: "تأسيس الشركات" }),
    t({ en: "Payroll", ar: "الرواتب" }),
    t({ en: "eInvoicing", ar: "الفوترة الإلكترونية" }),
  ];

  return (
    <div className="bg-ivory">
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/40 via-ivory/70 to-ivory" />
        <div className="container-tnsq relative pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div>
            <Reveal><span className="kicker gold-line">{t(HERO.eyebrow)}</span></Reveal>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.85rem] leading-[1.07] mt-6 text-ink">
              <MaskText
                lines={t(HERO.titleLines)}
                trigger="load"
                delay={0.15}
                accentIndex={2}
                accentClassName="text-navy italic"
              />
            </h1>
            <Reveal delay={160}>
              <p className="mt-6 text-base md:text-lg text-muted max-w-xl leading-relaxed">{t(HERO.lead)}</p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/#contact" data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-navy text-white font-semibold hover:bg-navy-600 transition-colors shadow-soft">
                  {t(HERO.ctaPrimary)}
                  <Arrow className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/tax-updates" data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full border border-navy/20 text-navy font-semibold hover:bg-white hover:border-navy/40 transition-colors">
                  {t(HERO.ctaSecondary)}
                </Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-white border border-gold/40 shadow-card"><ShieldCheck className="h-6 w-6 text-gold" /></div>
                  <div className="leading-tight">
                    <div className="font-semibold text-sm text-ink">{t(BRAND.agency)}</div>
                    <div className="text-muted text-xs" dir="ltr">FTA No. {BRAND.ftaNo}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-white border border-line shadow-card"><MapPin className="h-6 w-6 text-navy" /></div>
                  <div className="leading-tight">
                    <div className="font-semibold text-sm text-ink">{t({ en: "Sharjah, UAE", ar: "الشارقة، الإمارات" })}</div>
                    <div className="text-muted text-xs">{t({ en: "Serving the whole UAE", ar: "نخدم جميع الإمارات" })}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
          >
            <div className="absolute -inset-3 lg:-inset-4 rounded-[2rem] border border-gold/30 pointer-events-none" />
            <motion.div style={{ y: imgY }} className="relative rounded-[1.75rem] overflow-hidden shadow-lift ring-1 ring-line spotlight-frame">
              <motion.img style={{ scale: imgScale }} src={IMAGES.heroOffice} alt="TANSEEQ premium office" className="h-[440px] lg:h-[560px] w-full object-cover" />
            </motion.div>
            <motion.div style={{ y: badgeY }} className="absolute -bottom-6 start-6 lg:-start-8 rounded-2xl bg-white/90 backdrop-blur-md border border-line shadow-lift p-4 flex items-center gap-3">
              <div className="h-12 w-12 grid place-items-center rounded-xl bg-navy text-white font-display text-lg">FTA</div>
              <div className="leading-tight pe-2">
                <div className="text-[0.7rem] uppercase tracking-wider text-muted">{t(BRAND.agency)}</div>
                <div className="font-display text-xl text-navy" dir="ltr">No. {BRAND.ftaNo}</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="border-y border-line bg-white" data-testid="stats-band">
        <div className="container-tnsq grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 80}
              className={`px-5 py-9 text-center lg:text-start ${i !== 0 ? "lg:border-s border-line" : ""}`}>
              <div className="font-display text-4xl lg:text-5xl text-navy" dir="ltr">{s.value}</div>
              <div className="mt-2 text-xs md:text-sm text-muted leading-snug">{t(s.label)}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= TRUSTED / INDUSTRIES ================= */}
      <section className="py-10 border-b border-line bg-ivory" data-testid="industries-strip">
        <div className="container-tnsq flex flex-col lg:flex-row lg:items-center gap-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted shrink-0">
            {t({ en: "Trusted by businesses across", ar: "نخدم أعمالاً في قطاعات" })}
          </span>
          <div className="flex flex-wrap gap-2.5">
            {[
              { en: "Trading", ar: "التجارة" },
              { en: "Contracting", ar: "المقاولات" },
              { en: "Real Estate", ar: "العقارات" },
              { en: "F&B", ar: "المطاعم والأغذية" },
              { en: "E-commerce", ar: "التجارة الإلكترونية" },
              { en: "Professional Services", ar: "الخدمات المهنية" },
              { en: "Manufacturing", ar: "التصنيع" },
            ].map((s, i) => (
              <span key={i} className="text-sm font-medium text-ink/70 bg-surface border border-line rounded-full px-4 py-1.5">
                {t(s)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MARQUEE ================= */}
      <section className="py-4 lg:py-6 bg-ivory border-b border-line overflow-hidden" data-testid="marquee-section" aria-hidden="true">
        <Marquee items={marqueeItems} duration={40} />
      </section>

      {/* ================= SERVICES ================= */}
      <section id="services" className="py-24 lg:py-32" data-testid="services-section">
        <div className="container-tnsq">
          <div className="max-w-3xl">
            <Reveal><span className="kicker gold-line">{t({ en: "Everything under one roof", ar: "كل الخدمات تحت سقف واحد" })}</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink mt-5 leading-[1.15]">
                {t({ en: "A complete, integrated firm for tax, audit, accounting and setup.", ar: "شركة متكاملة للضرائب والتدقيق والمحاسبة وتأسيس الشركات." })}
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-muted text-base md:text-lg leading-relaxed">
                {t({ en: `${ALL_SERVICE_COUNT}+ services across four core areas — VAT & Corporate Tax, Excise, audit, bookkeeping, company formation, visas and more.`, ar: `أكثر من ${ALL_SERVICE_COUNT} خدمة في أربعة مجالات أساسية — ضريبة القيمة المضافة وضريبة الشركات والضريبة الانتقائية والتدقيق ومسك الدفاتر وتأسيس الشركات والتأشيرات والمزيد.` })}
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {SERVICE_GROUPS.map((g, i) => {
              const Icon = GROUP_ICONS[g.icon] || ShieldCheck;
              return (
                <Reveal key={g.id} delay={(i % 2) * 90} className="h-full">
                  <div className="group flex h-full flex-col rounded-2xl border border-line bg-white p-8 shadow-card hover:shadow-lift hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
                    <div className="flex items-center gap-4">
                      <div className="h-13 w-13 p-3 grid place-items-center rounded-xl bg-navy text-white"><Icon className="h-6 w-6" /></div>
                      <h3 className="font-display text-xl lg:text-2xl text-ink">{t(g.label)}</h3>
                    </div>
                    <p className="mt-4 text-sm text-muted leading-relaxed">{t(g.blurb)}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {g.services.slice(0, 6).map((s) => (
                        <Link key={s.id} to={`/services#${s.id}`}
                          className="text-xs font-medium text-ink/70 bg-surface hover:bg-navy hover:text-white border border-line rounded-full px-3 py-1.5 transition-colors">
                          {t(s.title)}
                        </Link>
                      ))}
                      {g.services.length > 6 && (
                        <span className="text-xs font-medium text-brandgreen bg-brandgreen/10 rounded-full px-3 py-1.5">
                          +{g.services.length - 6} {t({ en: "more", ar: "أخرى" })}
                        </span>
                      )}
                    </div>
                    <Link to={`/services#${g.id}`} data-testid={`group-card-${g.id}`}
                      className="mt-6 inline-flex items-center gap-2 text-navy font-semibold hover:gap-3 transition-all">
                      {t({ en: "View services", ar: "عرض الخدمات" })} <Arrow className="h-4 w-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={120} className="mt-10">
            <Link to="/services" data-testid="services-explore-all" className="inline-flex items-center gap-2 text-navy font-semibold hover:gap-3 transition-all">
              {t({ en: "Explore the full services catalogue", ar: "استعرض دليل الخدمات الكامل" })} <Arrow className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ================= MANIFESTO / APPROACH ================= */}
      <section className="relative py-24 lg:py-32 bg-navy-deep text-white overflow-hidden" data-testid="approach-section">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover" }} />
        <div className="container-tnsq relative grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
          <div className="lg:sticky lg:top-28 self-start">
            <span className="kicker gold-line text-gold">{t({ en: "How we work", ar: "منهج عملنا" })}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.12]">
              <MaskText
                lines={t({ en: ["We connect bookkeeping", "with tax compliance —", "as one discipline."], ar: ["نربط بين المحاسبة", "والامتثال الضريبي", "بوصفهما عملاً واحداً."] })}
                accentIndex={2}
                accentClassName="text-gold italic"
              />
            </h2>
            <div className="mt-8 relative rounded-[1.5rem] overflow-hidden ring-1 ring-white/15 spotlight-frame max-w-md">
              <img src={IMAGES.desk} alt="Clean financial records" className="w-full h-[240px] object-cover" />
            </div>
            <p className="mt-5 text-white/55 text-sm leading-relaxed max-w-md">
              {t({ en: "Strong compliance starts with reliable accounting evidence.", ar: "الامتثال القوي يبدأ من مستندات وسجلات محاسبية موثوقة." })}
            </p>
          </div>

          <div data-testid="manifesto-chapters">
            {APPROACH.map((a, i) => (
              <Reveal key={a.n} delay={i * 80}>
                <div className="group grid grid-cols-[auto_1fr] gap-6 lg:gap-10 py-8 border-t border-white/12">
                  <div className="font-display text-5xl lg:text-6xl text-white/15 group-hover:text-gold transition-colors duration-500 tabular-nums">{a.n}</div>
                  <div className="pt-1">
                    <h3 className="font-display text-2xl lg:text-3xl text-white">{t(a.title)}</h3>
                    <p className="mt-3 text-white/60 leading-relaxed max-w-xl">{t(a.desc)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/12" />
          </div>
        </div>
      </section>

      {/* ================= TAX UPDATES ================= */}
      <section id="updates" className="py-24 lg:py-32" data-testid="updates-preview-section">
        <div className="container-tnsq">
          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-end mb-14">
            <div>
              <Reveal><span className="kicker gold-line">{t({ en: "2026 Tax Update Hub", ar: "مركز تحديثات الضرائب 2026" })}</span></Reveal>
              <Reveal delay={80}>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink mt-5 leading-[1.15]">
                  {t({ en: "Important UAE tax changes you should know now.", ar: "أهم التغييرات الضريبية في الإمارات التي يجب أن تعرفها الآن." })}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={120} className="lg:text-end">
              <p className="text-muted leading-relaxed">
                {t({ en: "A dedicated feed for regulatory changes, deadlines and FTA / MoF initiatives — reviewed against official sources.", ar: "قسم مخصص للتغييرات التشريعية والمواعيد ومبادرات الهيئة ووزارة المالية، مراجَع وفق المصادر الرسمية." })}
              </p>
              <Link to="/tax-updates" data-testid="updates-open-hub" className="mt-4 inline-flex items-center gap-2 text-navy font-semibold hover:gap-3 transition-all">
                {t({ en: "Open the Update Hub", ar: "افتح مركز التحديثات" })} <Arrow className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TAX_UPDATES.slice(0, 4).map((u, i) => (
              <Reveal key={u.id} delay={(i % 4) * 80} className="h-full">
                <article data-testid={`update-preview-${u.id}`}
                  className={`flex h-full flex-col rounded-2xl border p-6 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-lift ${
                    u.tag === "NEW" ? "border-brandgreen/40 bg-brandgreen/[0.04]" : "border-line bg-white"
                  }`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-semibold tracking-wide text-navy/60" dir="ltr">{u.date.d} {t(u.date.m)} {u.date.y}</span>
                    {u.tag === "NEW" && <span className="text-[0.6rem] font-bold tracking-wider uppercase bg-brandgreen text-white px-2 py-0.5 rounded-full">{t({ en: "New", ar: "جديد" })}</span>}
                  </div>
                  <h3 className="font-display text-lg text-ink leading-snug">{t(u.title)}</h3>
                  <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-4 flex-1">{t(u.body)}</p>
                  {u.fact && <div className="mt-4 text-xs font-medium text-navy bg-surface rounded-xl px-3 py-2 leading-snug">{t(u.fact)}</div>}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section id="about" className="py-24 lg:py-32 bg-surface" data-testid="about-section">
        <div className="container-tnsq grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal><span className="kicker gold-line">{t({ en: "About TANSEEQ", ar: "عن تنسيق" })}</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink mt-5 leading-[1.15]">
                {t({ en: "Tax support built around evidence, deadlines and clear communication.", ar: "دعم ضريبي مبني على المستندات والمواعيد والتواصل الواضح." })}
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-muted leading-relaxed">
                {t({ en: "Established in 2018, TANSEEQ brings together accountants and auditors with more than 18 years of combined experience. As one of the earliest Tax Agencies accredited by the Federal Tax Authority, we help businesses organize records, understand their obligations and file with a practical, compliance-first approach — professional without being complicated.", ar: "تأسست تنسيق عام 2018، وتضم نخبة من المحاسبين ومدققي الحسابات بخبرة تتجاوز 18 عاماً. وبصفتنا من أوائل الوكالات الضريبية المعتمدة لدى الهيئة الاتحادية للضرائب، نساعد الشركات على تنظيم سجلاتها وفهم التزاماتها وتقديم إقراراتها بمنهج عملي يضع الامتثال في المقدمة — احتراف بلا تعقيد." })}
              </p>
            </Reveal>
            <Reveal delay={180}>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {[
                  { v: "2018", l: { en: "Established", ar: "سنة التأسيس" } },
                  { v: "+18", l: { en: "Years of experience", ar: "عاماً من الخبرة" } },
                  { v: t({ en: "Early", ar: "من الأوائل" }), l: { en: "FTA-accredited agency", ar: "وكالة معتمدة لدى الهيئة" } },
                ].map((h, i) => (
                  <div key={i} className="rounded-2xl border border-line bg-white p-4 text-center shadow-card">
                    <div className="font-display text-2xl md:text-3xl text-navy" dir="ltr">{h.v}</div>
                    <div className="mt-1 text-[0.7rem] md:text-xs text-muted leading-snug">{t(h.l)}</div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-gold/40 bg-white px-6 py-5 shadow-card">
                <div className="h-14 w-14 grid place-items-center rounded-xl bg-navy text-white font-display text-lg">FTA</div>
                <div>
                  <div className="text-sm text-muted">{t(BRAND.agency)}</div>
                  <div className="font-display text-2xl text-navy" dir="ltr">{BRAND.ftaNo}</div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <div className="rounded-[1.75rem] overflow-hidden shadow-lift ring-1 ring-line">
              <img src={IMAGES.advisor} alt="TANSEEQ advisor" className="w-full h-[500px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= PACKAGES ================= */}
      <section id="packages" className="py-24 lg:py-32 bg-surface scroll-mt-24" data-testid="packages-section">
        <div className="container-tnsq">
          <div className="max-w-3xl">
            <Reveal><span className="kicker gold-line">{t({ en: "Our packages", ar: "باقاتنا" })}</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink mt-5 leading-[1.15]">
                {t({ en: "Monthly plans that grow with your business.", ar: "باقات شهرية تنمو مع أعمالك." })}
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-muted text-base md:text-lg leading-relaxed">{t(PRICE_NOTE)}</p>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-start">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 80} className="h-full">
                <div data-testid={`package-${p.id}`}
                  className={`relative flex h-full flex-col rounded-[1.5rem] bg-white p-7 transition-shadow duration-300 ${
                    p.popular ? "border-2 border-gold shadow-lift" : "border border-line shadow-card hover:shadow-lift"
                  }`}>
                  {p.popular && (
                    <span className="absolute -top-3 start-6 inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wider bg-gold text-navy-deep px-3 py-1 rounded-full">
                      <Sparkles className="h-3 w-3" /> {t({ en: "Most popular", ar: "الأكثر طلباً" })}
                    </span>
                  )}
                  <h3 className="font-display text-2xl text-navy">{t(p.name)}</h3>
                  <p className="mt-1 text-sm text-muted">{t(p.tier)}</p>
                  <div className="mt-4 rounded-xl bg-surface border border-line px-4 py-3 text-center">
                    <div className="font-display text-lg text-ink">{t({ en: "Custom pricing", ar: "تسعير مخصّص" })}</div>
                    <div className="text-[0.7rem] text-muted mt-0.5 leading-snug">{t(PRICE_NOTE)}</div>
                  </div>
                  <a href="#contact" data-testid={`package-cta-${p.id}`}
                    className={`mt-4 inline-flex items-center justify-center h-11 rounded-full font-semibold transition-colors ${
                      p.popular ? "bg-navy text-white hover:bg-navy-600" : "border border-navy/25 text-navy hover:bg-navy hover:text-white"
                    }`}>
                    {t({ en: "Request this package", ar: "اطلب هذه الباقة" })}
                  </a>
                  <div className="mt-6 space-y-5">
                    {p.groups.map((grp, gi) => (
                      <div key={gi}>
                        <div className="text-[0.7rem] font-bold uppercase tracking-wider text-brandgreen mb-2.5">{t(grp.title)}</div>
                        <ul className="space-y-2">
                          {grp.items.map((key) => (
                            <li key={key} className="flex gap-2.5 items-start">
                              <Check className="h-4 w-4 text-brandgreen shrink-0 mt-0.5" />
                              <span className="text-[0.82rem] text-ink/75 leading-snug">{t(FEATURES[key])}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA BAND ================= */}
      <section className="py-16" data-testid="cta-band">
        <div className="container-tnsq">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-navy-deep px-8 py-12 md:px-14 md:py-14">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-2 text-gold text-sm font-semibold"><Sparkles className="h-4 w-4" /> {t({ en: "Free first consultation", ar: "استشارة أولى مجانية" })}</span>
                  <h2 className="font-display text-3xl md:text-4xl text-white mt-3 leading-tight">
                    {t({ en: "Need to review your VAT, Corporate Tax or accounting position?", ar: "محتاج تراجع موقف ضريبة القيمة المضافة أو ضريبة الشركات أو الحسابات؟" })}
                  </h2>
                </div>
                <a href={`https://wa.me/${BRAND.phoneRaw}`} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 h-13 px-8 py-3.5 rounded-full bg-gold text-navy-deep font-semibold hover:bg-white transition-colors shrink-0">
                  {t({ en: "Talk to us on WhatsApp", ar: "تحدّث معنا على واتساب" })}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section id="contact" className="py-24 lg:py-32 bg-surface" data-testid="contact-section">
        <div className="container-tnsq grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
          <div>
            <Reveal><span className="kicker gold-line">{t({ en: "Contact", ar: "تواصل معنا" })}</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink mt-5 leading-[1.15]">
                {t({ en: "Tell us what you need.", ar: "أخبرنا بما تحتاجه." })}
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-muted leading-relaxed">
                {t({ en: "Share the basic facts and we will identify the right next step for your VAT, Corporate Tax or accounting position.", ar: "شاركنا البيانات الأساسية ونحدد معك الخطوة الصحيحة لموقف ضريبة القيمة المضافة أو ضريبة الشركات أو الحسابات." })}
              </p>
            </Reveal>

            <div className="mt-9 space-y-5">
              {[
                { icon: MapPin, label: { en: "Office", ar: "المكتب" }, value: t(BRAND.address) },
                { icon: Phone, label: { en: "Phone & WhatsApp", ar: "الهاتف وواتساب" }, value: BRAND.phone, href: `tel:${BRAND.phoneRaw}`, ltr: true },
                { icon: Mail, label: { en: "Email", ar: "البريد الإلكتروني" }, value: BRAND.email, href: `mailto:${BRAND.email}`, ltr: true },
                { icon: Clock, label: { en: "Working hours", ar: "ساعات العمل" }, value: t(BRAND.hours) },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 70}>
                  <div className="flex gap-4">
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-white border border-line text-navy shrink-0 shadow-card"><item.icon className="h-5 w-5" /></div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t(item.label)}</div>
                      {item.href ? (
                        <a href={item.href} className="text-ink font-medium hover:text-navy transition-colors" dir={item.ltr ? "ltr" : undefined}>{item.value}</a>
                      ) : (<div className="text-ink font-medium">{item.value}</div>)}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={120}>
            <div className="rounded-[1.75rem] bg-white border border-line p-7 md:p-9 shadow-lift">
              <ContactForm />
              <p className="mt-5 text-xs text-muted leading-relaxed flex gap-2">
                <Check className="h-4 w-4 text-brandgreen shrink-0 mt-0.5" />
                {t({ en: "Your details are used only to respond to your request.", ar: "تُستخدم بياناتك فقط للرد على طلبك." })}
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
